import {RestService} from "@/services/rest/RestService";
import {ContentBlockEndpointsApi} from "@/api/pharma-cms-content-block";
import {
    BannerContentV2RestDto,
    BannerInSlotContentRestDto,
    ButtonContentRestDto,
    ContactFormContentRestDto,
    ContentOverviewRestDto,
    DocumentContentRestDto,
    HtmlContentRestDto,
    ImageContentRestDto,
    PageReferencesContentRestDto,
    PharmacyInfoContentRestDto,
    ProductFilterContentRestDto,
    RowSeparatorContentRestDto,
    SymbolContentRestDto,
    TeamInfoContentRestDto,
    TextContentRestDto,
    TextWithImageContentRestDto,
    TextWithSymbolContentRestDto,
    TextWithTitleContentRestDto
} from "@/api/pharma-cms-content-block/models";
import {StatusEnum} from "@/api/enums/status-enum";
import {BannerContentV2RestDtoModel} from "@/models/api/pharma-cms-content-block/BannerContentV2RestDtoModel";
import {AxiosResponse} from "axios";
import {TextWithImageContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextWithImageContentRestDtoModel";
import {TextWithTitleContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextWithTitleContentRestDtoModel";
import {ButtonContentRestDtoModel} from "@/models/api/pharma-cms-content-block/ButtonContentRestDtoModel";
import {ContactFormContentRestDtoModel} from "@/models/ContactFormContentRestDtoModel";
import {DocumentContentRestDtoModel} from "@/models/api/pharma-cms-content-block/DocumentContentRestDtoModel";
import {HtmlContentRestDtoModel} from "@/models/api/pharma-cms-content-block/HtmlContentRestDtoModel";
import {ImageContentRestDtoModel} from "@/models/api/pharma-cms-content-block/ImageContentRestDtoModel";
import {PageReferencesContentRestDtoModel} from "@/models/PageReferencesContentRestDtoModel";
import {PharmacyInfoContentRestDtoModel} from "@/models/api/pharma-cms-content-block/PharmacyInfoContentRestDtoModel";
import {ProductFilterContentRestDtoModel} from "@/models/ProductFilterContentRestDtoModel";
import {RowSeparatorContentRestDtoModel} from "@/models/RowSeparatorContentRestDtoModel";
import {SymbolContentRestDtoModel} from "@/models/api/pharma-cms-content-block/SymbolContentRestDtoModel";
import {TeamInfoContentRestDtoModel} from "@/models/TeamInfoContentRestDtoModel";
import {TextContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextContentRestDtoModel";
import {TextWithSymbolContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextWithSymbolContentRestDtoModel";

export class NormalContentBlockRestService extends RestService {

    private apiService: ContentBlockEndpointsApi;

    protected constructor() {
        super();

        this.apiService = new ContentBlockEndpointsApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cmsUrl);
    }

    public async createNormalContentBlockBannerInSlot(contentBlock: BannerInSlotContentRestDto, type: string): Promise<void> {
        await this.apiService.createNormalContentBlockBannerInSlot(contentBlock, type);
    }

    public async findContentBlockOverview(type: string, filterStatusCode?: StatusEnum, filterLibraryCode?: string, filterLinked?: boolean): Promise<ContentOverviewRestDto[]> {
        const response = await this.apiService
            .findNormalContentBlocks(type, undefined, filterStatusCode, filterLibraryCode, filterLinked);

        return response.data;
    }

    public async deleteContentBlock(type: string, id: string): Promise<void> {
        await this.apiService.deleteNormalContentBlock(type, id);
    }

    public async createBannerV2(type: string, dto: BannerContentV2RestDto): Promise<void> {
        await this.apiService.createNormalContentBlockBannerV2(dto, type);
    }

    public async getBannerV2(type: string, id: string): Promise<BannerContentV2RestDtoModel> {
        const response = await this.apiService
            .getNormalContentBlockBannerV2(type, id);

        return response.data;
    }

    public async updateBannerV2(id: string, type: string, dto: BannerContentV2RestDto): Promise<void> {
        await this.apiService.updateNormalContentBlockBannerV2(dto, type, id);
    }

    public async getNormalContentBlockTextWithImage(type: string, id: string): Promise<TextWithImageContentRestDto> {
        const response: AxiosResponse<TextWithImageContentRestDto> = await this.apiService.getNormalContentBlockTextWithImage(type, id);

        if (response.status === 204) {
            return TextWithImageContentRestDtoModel.createWithDefaults();
        }

        // Normalized like findTextWithImage(): the caller (CbFormTextWithImage) PUTs this object
        // back as-is, so a raw response would send the required image fields the API left out
        // straight back to it, which the API answers with a 500.
        return TextWithImageContentRestDtoModel.cloneFrom(response.data);
    }

    public async getNormalContentBlockTextWithTitle(type: string, id: string): Promise<TextWithTitleContentRestDto> {
        const response: AxiosResponse<TextWithImageContentRestDto> = await this.apiService.getNormalContentBlockTextWithTitle(type, id);

        if (response.status === 204) {
            return TextWithTitleContentRestDtoModel.createWithDefaults();
        }

        return response.data;
    }

    public async updateNormalContentBlockTextWithTitle(body: TextWithTitleContentRestDto, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockTextWithTitle(body, type, id);
    }

    public async createNormalContentBlockTextWithTitle(body: TextWithTitleContentRestDto, type: string): Promise<void> {
        await this.apiService.createNormalContentBlockTextWithTitle(body, type);
    }

    // The find/create/save triples below back the content-block structure components of the WYSIWYG
    // editor (CbText, CbImage, …). Every find normalizes through the model instead of casting the raw
    // payload: the response is sent back verbatim on save, so fields the API omits must get their
    // defaults applied here.

    public async findButton(type: string, id: string): Promise<ButtonContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockButton(type, id);

        return ButtonContentRestDtoModel.cloneFrom(response.data as ButtonContentRestDto);
    }

    public async createButton(cb: ButtonContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockButton(cb, type);

        return response.data.id as string;
    }

    public async saveButton(cb: ButtonContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockButton(cb, type, id);
    }

    public async findContactForm(type: string, id: string): Promise<ContactFormContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockContactForm(type, id);

        return ContactFormContentRestDtoModel.cloneFrom(response.data as ContactFormContentRestDto);
    }

    public async createContactForm(cb: ContactFormContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockContactForm(cb, type);

        return response.data.id as string;
    }

    public async saveContactForm(cb: ContactFormContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockContactForm(cb, type, id);
    }

    public async findDocument(type: string, id: string): Promise<DocumentContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockDocument(type, id);

        return DocumentContentRestDtoModel.cloneFrom(response.data as DocumentContentRestDto);
    }

    public async createDocument(cb: DocumentContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockDocument(cb, type);

        return response.data.id as string;
    }

    public async saveDocument(cb: DocumentContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockDocument(cb, type, id);
    }

    public async findHtml(type: string, id: string): Promise<HtmlContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockHtml(type, id);

        return HtmlContentRestDtoModel.cloneFrom(response.data as HtmlContentRestDto);
    }

    public async createHtml(cb: HtmlContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockHtml(cb, type);

        return response.data.id as string;
    }

    public async saveHtml(cb: HtmlContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockHtml(cb, type, id);
    }

    public async findImage(type: string, id: string): Promise<ImageContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockImage(type, id);

        return ImageContentRestDtoModel.cloneFrom(response.data as ImageContentRestDto);
    }

    public async createImage(cb: ImageContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockImage(cb, type);

        return response.data.id as string;
    }

    public async saveImage(cb: ImageContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockImage(cb, type, id);
    }

    public async findPageReferences(type: string, id: string): Promise<PageReferencesContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockPageReferences(type, id);

        return PageReferencesContentRestDtoModel.cloneFrom(response.data as PageReferencesContentRestDto);
    }

    public async createPageReferences(cb: PageReferencesContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockPageReferences(cb, type);

        return response.data.id as string;
    }

    public async savePageReferences(cb: PageReferencesContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockPageReferences(cb, type, id);
    }

    public async findPharmacyInfo(type: string, id: string): Promise<PharmacyInfoContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockPharmacyInfo(type, id);

        return PharmacyInfoContentRestDtoModel.cloneFrom(response.data as PharmacyInfoContentRestDto);
    }

    public async createPharmacyInfo(cb: PharmacyInfoContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockPharmacyInfo(cb, type);

        return response.data.id as string;
    }

    public async savePharmacyInfo(cb: PharmacyInfoContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockPharmacyInfo(cb, type, id);
    }

    public async findProductFilter(type: string, id: string): Promise<ProductFilterContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockProductFilter(type, id);

        return ProductFilterContentRestDtoModel.cloneFrom(response.data as ProductFilterContentRestDto);
    }

    public async createProductFilter(cb: ProductFilterContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockProductFilter(cb, type);

        return response.data.id as string;
    }

    public async saveProductFilter(cb: ProductFilterContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockProductFilter(cb, type, id);
    }

    public async findRowSeparator(type: string, id: string): Promise<RowSeparatorContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockRowSeparator(type, id);

        return RowSeparatorContentRestDtoModel.cloneFrom(response.data as RowSeparatorContentRestDto);
    }

    public async createRowSeparator(cb: RowSeparatorContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockRowSeparator(cb, type);

        return response.data.id as string;
    }

    public async saveRowSeparator(cb: RowSeparatorContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockRowSeparator(cb, type, id);
    }

    public async findSymbol(type: string, id: string): Promise<SymbolContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockSymbol(type, id);

        return SymbolContentRestDtoModel.cloneFrom(response.data as SymbolContentRestDto);
    }

    public async createSymbol(cb: SymbolContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockSymbol(cb, type);

        return response.data.id as string;
    }

    public async saveSymbol(cb: SymbolContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockSymbol(cb, type, id);
    }

    public async findTeamInfo(type: string, id: string): Promise<TeamInfoContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockTeamInfo(type, id);

        return TeamInfoContentRestDtoModel.cloneFrom(response.data as TeamInfoContentRestDto);
    }

    public async createTeamInfo(cb: TeamInfoContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockTeamInfo(cb, type);

        return response.data.id as string;
    }

    public async saveTeamInfo(cb: TeamInfoContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockTeamInfo(cb, type, id);
    }

    public async findText(type: string, id: string): Promise<TextContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockText(type, id);

        return TextContentRestDtoModel.cloneFrom(response.data as TextContentRestDto);
    }

    public async createText(cb: TextContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockText(cb, type);

        return response.data.id as string;
    }

    public async saveText(cb: TextContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockText(cb, type, id);
    }

    public async findTextWithImage(type: string, id: string): Promise<TextWithImageContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockTextWithImage(type, id);

        return TextWithImageContentRestDtoModel.cloneFrom(response.data as TextWithImageContentRestDto);
    }

    public async createTextWithImage(cb: TextWithImageContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockTextWithImage(cb, type);

        return response.data.id as string;
    }

    public async saveTextWithImage(cb: TextWithImageContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockTextWithImage(cb, type, id);
    }

    public async findTextWithSymbol(type: string, id: string): Promise<TextWithSymbolContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockTextWithSymbol(type, id);

        return TextWithSymbolContentRestDtoModel.cloneFrom(response.data as TextWithSymbolContentRestDto);
    }

    public async createTextWithSymbol(cb: TextWithSymbolContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockTextWithSymbol(cb, type);

        return response.data.id as string;
    }

    public async saveTextWithSymbol(cb: TextWithSymbolContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockTextWithSymbol(cb, type, id);
    }

    public async findTextWithTitle(type: string, id: string): Promise<TextWithTitleContentRestDtoModel> {
        const response = await this.apiService.getNormalContentBlockTextWithTitle(type, id);

        return TextWithTitleContentRestDtoModel.cloneFrom(response.data as TextWithTitleContentRestDto);
    }

    public async createTextWithTitle(cb: TextWithTitleContentRestDtoModel, type: string): Promise<string> {
        const response = await this.apiService.createNormalContentBlockTextWithTitle(cb, type);

        return response.data.id as string;
    }

    public async saveTextWithTitle(cb: TextWithTitleContentRestDtoModel, type: string, id: string): Promise<void> {
        await this.apiService.updateNormalContentBlockTextWithTitle(cb, type, id);
    }
}
